import * as C from '../common/constants';
import React, { useState, useRef, useEffect } from 'react';
import { COLORS, FONTS, FONT_WEIGHT } from '../common/theme';
import { makeStyles, createStyles, StyleRules } from '@mui/styles';
import { useSelector, useDispatch } from 'react-redux';
import {
  clearData,
  showLoginPopup,
  hideLoginPopup,
} from '../common/app/actions';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import DraftsDialog from './DraftsDialog';
import { storeAuthData } from '../common/app/actions';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  getAuth,
  signInWithPopup,
  createUserWithEmailAndPassword,
  onAuthStateChanged,
  signOut,
} from 'firebase/auth';

const useStyles = makeStyles(
  (theme): StyleRules =>
    createStyles({
      rightStyle: {
        position: 'absolute',
        right: 0,
        top: 0,
        display: 'flex',
        flexDirection: 'row',
      },
      titleStyle: {
        fontWeight: FONT_WEIGHT.medium,
        fontSize: '18px',
        fontFamily: FONTS.garamond,
        color: COLORS.appBlue,
        whiteSpace: 'nowrap',
        padding: '18px 30px',
        background: COLORS.appMenuSand,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        textTransform: 'uppercase',
        boxShadow: '0px 3px 6px rgba(0,0,0,.16)',
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            color: COLORS.appBlack,
            background: COLORS.appMenuHoverSand,
            transition: '0.5s ease',
          },
        },
      },
      imageWrapperStyle: {
        width: '110px',
        height: '50px',
        boxShadow: '0px 3px 6px rgba(0,0,0,.16)',
        background: COLORS.appMenuSand,
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        boxSizing: 'border-box',
        padding: '8px',
      },
      imageStyle: {
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            filter: 'brightness(120%)',
            transition: '0.5s ease',
          },
        },
      },
      profileStyle: {
        width: '34px',
        height: '100%',
        background: COLORS.appBlue,
        borderRadius: '50%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textTransform: 'capitalize',
        color: COLORS.white,
        fontWeight: FONT_WEIGHT.medium,
        fontSize: '18px',
        fontFamily: FONTS.garamond,
        cursor: 'pointer',
        '@media (hover: hover) and (pointer: fine)': {
          '&:hover': {
            filter: 'brightness(120%)',
            transition: '0.5s ease',
          },
        },
      },
      popperStyle: {
        zIndex: 1000,
      },
      [`@media (max-width: 720px)`]: {
        titleStyle: {
          borderRadius: 0,
          fontSize: '14px',
          width: '50%',
          boxSizing: 'border-box',
        },
        rightStyle: {
          top: '-37px',
        },
        imageWrapperStyle: {
          background: 'none',
          boxShadow: 'none',
          width: '34px',
          height: '34px',
          padding: '4px',
        },
      },
      [`@media (max-width: 600px)`]: {
        titleStyle: {
          padding: '16px 20px',
        },
      },
      [`@media (max-width: 320px)`]: {
        titleStyle: {
          fontSize: '12px',
          width: '50%',
          padding: '16px 20px',
        },
      },
      [`@media (max-width: 250px)`]: {
        titleStyle: {
          fontSize: '12px',
          width: '100%',
        },
      },
    })
);

const HeaderLoginButton = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { search, pathname } = useLocation();

  const authData = useSelector((state: any) => state.app.authData);

  const anchorRef = useRef(null);
  const [isDraftsDialogOpen, setDraftsDialog] = useState(false);
  const [isMenuOpen, setOpen] = useState(false);

  const auth = getAuth();

  const checkAuth = () => {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        // const uid = auth.currentUser.uid;

        dispatch(storeAuthData(user));
      }
    });
  };

  useEffect(() => {
    if (!authData) {
      checkAuth();
    }
  }, [authData]);

  useEffect(() => {
    setDraftsDialog(false);
  }, [pathname]);

  const onLoginClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();

    dispatch(showLoginPopup());
  };

  const onLogoutClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
    event.preventDefault();

    signOut(auth)
      .then(() => {
        setOpen(false);
        dispatch(hideLoginPopup());
        dispatch(clearData(C.DRAFTS_DATA));
        dispatch(clearData(C.AUTH_DATA));
      })
      .catch((error) => {
        // An error happened.
      });
  };

  const onDraftsClick = (event?: React.SyntheticEvent) => {
    event?.stopPropagation();
    event?.preventDefault();

    setOpen(false);
    setDraftsDialog(true);
  };

  const onDraftsClose = (event?: React.SyntheticEvent) => {
    event?.stopPropagation();
    event?.preventDefault();

    setDraftsDialog(false);
  };

  const onCircleClick = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = (event?: Event | React.SyntheticEvent) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event?.target as HTMLElement)
    ) {
      return;
    }

    setOpen(false);
  };

  const getLoginButton = () => {
    if (!authData) {
      return (
        <div className={classes.titleStyle} onClick={onLoginClick}>
          {'IENĀKT'}
        </div>
      );
    }

    const email = authData?.email;
    const letter = email?.charAt(0);

    return (
      <>
        <div className={classes.imageWrapperStyle} ref={anchorRef}>
          <div className={classes.profileStyle} onClick={onCircleClick}>
            {letter}
          </div>
        </div>
        <Popper
          open={isMenuOpen}
          anchorEl={anchorRef.current}
          role={undefined}
          placement="bottom-start"
          transition
          // disablePortal
          className={classes.popperStyle}
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === 'bottom-start' ? 'left top' : 'left bottom',
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList
                    autoFocusItem={isMenuOpen}
                    id="composition-menu"
                    aria-labelledby="composition-button"
                    // onKeyDown={handleListKeyDown}
                  >
                    {/* <MenuItem onClick={onProfileClick}>PROFILS</MenuItem> */}
                    <MenuItem onClick={onDraftsClick}>
                      ANKETU MELNRAKSTI
                    </MenuItem>
                    <MenuItem onClick={onLogoutClick}>IZIET</MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </>
    );
  };

  return (
    <>
      <div className={classes.rightStyle}>{getLoginButton()}</div>
      {isDraftsDialogOpen && authData && (
        <DraftsDialog onClose={onDraftsClose} />
      )}
    </>
  );
};

export default HeaderLoginButton;
